import {
    MODE_DELIVERY,
    MODE_ONSITE,
    MODE_TAKEAWAY,
} from "../constants/constants";
import {
    CategoryItem,
    Ingredient,
    OrderItem,
    PurchaseOffers,
    RestaurantCart,
    SelectedArticle,
    SelectedIngredient,
    TypeOrderPrice,
} from "../interfaces/Catalogue";
import { PanierDiscount } from "../interfaces/loyality.interface";
import { isPurchaseOfferActive } from "./loyaltyUtils";

export const getTotalOrder = (categories: CategoryItem[]): number => {

    const calculatedTotal =
        categories?.length > 0
            ? categories?.reduce((total: number, category) => {
                if (category?.purchaseOffers && isPurchaseOfferActive(category.purchaseOffers)) {
                    const { paid } = getPaidFreeCounts(category.purchaseOffers, category?.quantity || 0);
                    return (
                        total + (category?.finalUnitPrice || 0) * paid
                    );
                }
                return (
                    total + (category?.finalUnitPrice || 0) * (category?.quantity || 0)
                );
            }, 0)
            : 0;

    return parseFloat(calculatedTotal.toFixed(2));
};




export const calculatediscountedPrice = (
    priceByTypeOrder: number,
    rewardDiscountType: number,
    rewardDiscountValue: number
): number => {
    const discountedPrice =
        rewardDiscountType === 0
            ? priceByTypeOrder - (priceByTypeOrder * rewardDiscountValue) / 100
            : priceByTypeOrder - rewardDiscountValue;

    return parseFloat((discountedPrice < 0 ? 0 : discountedPrice).toFixed(2));
};

export const calculateCartDiscount = (
    price: number,
    panierDiscount: PanierDiscount
): number => {
    return panierDiscount.cartRewardDiscountType === 0
        ? (price * panierDiscount.cartRewardDiscountValue) / 100 // Percentage discount
        : panierDiscount.cartRewardDiscountValue; // Fixed amount discount
};


export const calculateSelectedArticlesPrice = (
    selectedArticles: SelectedArticle[] = [],
    orderType: number | null,
    afterDiscount = false,
    excludeFree = true
): number =>
    selectedArticles.reduce((sum, article) => {
        if (excludeFree && article.selectedMenuItem.isFree) {
            return sum;
        }


        return (
            sum +
            getSelectedArticlePrice(
                article,
                afterDiscount,
                orderType
            )
        );
    }, 0);

export const calculateMenuFinalPrice = (
    order: OrderItem[],
    orderType: number | null,
    afterDiscount = false
): number =>
    order.reduce(
        (total, item) =>
            total +
            calculateSelectedArticlesPrice(
                item.selectedArticles,
                orderType,
                afterDiscount
            ),
        0
    );


/**
 * Compute the full price of a single SelectedArticle
 * (base + ingredients) × requested quantity
 */
const getSelectedArticlePrice = (
    selectedArticle: SelectedArticle,
    afterDiscount: boolean,
    orderType: number | null

): number => {
    const { selectedMenuItem, selectedIngredients } = selectedArticle;

    const priceByOrder =
        findPriceByTypeOrder(selectedMenuItem?.typeOrderPrices, orderType) ?? 0;

    const basePrice = afterDiscount
        ? selectedMenuItem?.discountedPrice ?? priceByOrder
        : priceByOrder;

    const ingredientsPrice =
        selectedIngredients?.reduce(
            (sum, { ingredient, quantity = 1 }) =>
                sum +
                getPriceByTypeOrderForComposition(orderType, ingredient) * quantity,
            0
        ) ?? 0;

    return (basePrice + ingredientsPrice) * (selectedMenuItem?.quantity ?? 1);
};


export const calculateTotalArticlesPrice = (
    categories: CategoryItem[],
    orderType: number,
    afterDiscount: boolean
): number => {
    let subTotal = 0;

    categories?.forEach((categoryItem) => {
        subTotal +=
            calculateItemTotalPrice(categoryItem, orderType, afterDiscount) || 0;
    });

    return subTotal;
};

export const calculateItemTotalPrice = (
    categoryItem: CategoryItem,
    orderType: number,
    afterDiscount: boolean
): number | null => {
    let totalPrice: number;

    // isMenu
    if (categoryItem.order) {
        const finalUnitPrice = calculateMenuFinalPrice(
            categoryItem.order,
            orderType,
            afterDiscount
        );
        totalPrice = finalUnitPrice * (categoryItem.quantity ?? 0);
    } else {
        // isArticle

        const { paid } = getPaidFreeCounts(
            categoryItem.purchaseOffers,
            categoryItem.quantity || 0
        );

        if (afterDiscount) {
            totalPrice =
                (categoryItem.discountedPrice || categoryItem.finalUnitPrice || 0) *
                paid;
        } else {
            totalPrice =
                (categoryItem.finalUnitPrice ?? 0) * paid;
        }
    }

    return Math.max(totalPrice, 0);
};

export const calculateNewTotalUserPts = (
    totalLoyaltyPtsConsomed: number,
    selectedMenuItem: { rewardTotalPtRequired: number; quantity?: number },
    orderCategoryItem?: { quantity?: number }
): number => {
    return (
        (totalLoyaltyPtsConsomed || 0) +
        selectedMenuItem.rewardTotalPtRequired *
        (selectedMenuItem.quantity || 0) *
        (orderCategoryItem?.quantity || 0)
    );
};

export const calculateArticleTotalPriceWithOffers = (
    purchaseOffers: PurchaseOffers,
    finalUnitPrice: number,
    articleCount: number
): number => {
    const { paid } = getPaidFreeCounts(purchaseOffers, articleCount);
    return paid * finalUnitPrice;
};


// --------------------------------------------------------
// --------------------------------------------------------

//  Get price by type order for article
export const findPriceByTypeOrder = (
    typeOrderCategory: TypeOrderPrice[],
    typeOrder?: number | null 
): number => {
    const item = typeOrderCategory?.find(
        (orderCategory) => orderCategory.type === typeOrder
    );
    return item?.price || 0;
};

// Get price by type order for composition (ingredient)
export const getPriceByTypeOrderForComposition = (
    typeOrder: number | null ,
    articleComposition: Ingredient
): number => {
    switch (typeOrder) {
        case MODE_ONSITE: // Sur Place
            return articleComposition.priceOnSite ?? 0;
        case MODE_TAKEAWAY: // A Emporter
            return articleComposition.priceEmporter ?? 0;
        case MODE_DELIVERY: // Livraison
            return articleComposition.priceDelivery ?? 0;
        default:
            return 0;
    }
};

// Get paid and free counts from purchase offers
export const getPaidFreeCounts = (
    offers: PurchaseOffers,
    quantity: number
): { paid: number; free: number } => {

    if (!isPurchaseOfferActive(offers)) {
        return { paid: quantity, free: 0 };
    }

    const nbPurchased = Number(offers.nbPurchased);
    const nbOffered = Number(offers.nbOffered);

    if (nbPurchased === 0 || nbOffered === 0) {
        return { paid: quantity, free: 0 };
    }

    const blockSize = nbPurchased + nbOffered;
    const fullBlocks = Math.floor(quantity / blockSize);
    const remainder = quantity % blockSize;

    const paid = fullBlocks * nbPurchased + Math.min(remainder, nbPurchased);
    const free = fullBlocks * nbOffered + Math.max(0, remainder - nbPurchased);

    return { paid, free };
};

//  Convert centimes to euros
export const centimesToEuros = (centimes: number): number => centimes / 100;

/**
 * Formats amount to 2 decimal places
 */
export const formatCurrency = (amount?: number): string => {
    return (amount ?? 0).toFixed(2);
};

// Calculates the total price of selected ingredients
const calculateIngredientsPrice = (
    selectedIngredients: SelectedIngredient[] = [],
    mode: number | null
): number =>
    selectedIngredients.reduce(
        (total, { ingredient }) =>
            total + getPriceByTypeOrderForComposition(mode, ingredient),
        0
    );

// Calculates the price of a single category article (base + ingredients)
const calculateCategoryArticlePrice = (
    categoryItem: CategoryItem,
    mode: number | null
): number => {
    const basePrice =
        findPriceByTypeOrder(categoryItem.typeOrderCategory, mode) ?? 0;

    const ingredientsPrice = calculateIngredientsPrice(
        categoryItem.selectedIngredients,
        mode
    );

    return (basePrice + ingredientsPrice) * (categoryItem.quantity ?? 0);
};

// Calculates the price of an article inside a menu level
const calculateLevelArticlePrice = (
    selectedArticle: SelectedArticle,
    mode: number
): number => {
    
    const basePrice =
        findPriceByTypeOrder(
            selectedArticle.selectedMenuItem.typeOrderPrices,
            mode
        ) ?? 0;

    const ingredientsPrice = calculateIngredientsPrice(
        selectedArticle.selectedIngredients,
        mode
    );

    return (basePrice + ingredientsPrice) * (selectedArticle.selectedMenuItem.quantity ?? 0);
};

// Calculates the total price of a menu level
const calculateLevelPrice = (
    orderItem: OrderItem,
    mode: number
): number =>
    (orderItem.selectedArticles ?? []).reduce(
        (total, selectedArticle) =>
            total + calculateLevelArticlePrice(selectedArticle, mode),
        0
    );

// Calculates the total price of a menu
const calculateMenuPrice = (
    categoryItem: CategoryItem,
    mode: number
): number =>
    (categoryItem.order ?? []).reduce(
        (total, orderItem) =>
            total + calculateLevelPrice(orderItem, mode),
        0
    ) * (categoryItem.quantity ?? 0);

// Calculates the total cart price for a restaurant
export const calculTotalCart = (
    restaurantCart: RestaurantCart | null
): number => {
    
    if (!restaurantCart) return 0;

    const { categories = [], mode } = restaurantCart;

    const total = categories.reduce((sum, categoryItem) => {
        if (!categoryItem.order) {
            // Article
            return sum + calculateCategoryArticlePrice(categoryItem, mode);
        }
        // Menu
        return sum + calculateMenuPrice(categoryItem, mode);
    }, 0);

    return parseFloat(total.toFixed(2));
};
